import { RiImageLine, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { Article, ArticleCard } from '@/components/widgets/o/ArticleCard/ArticleCard';
import { LinkCard } from '@/components/widgets/o/LinkCard/LinkCard';
import { useKnowledgePortal } from '@/hooks/useKnowledgePortal';

type ArticleListProps = {
  articles: Article[];
  onArticleClick?: (article: Article) => void;
};

export const KnowledgeCards = ({ articles, onArticleClick }: ArticleListProps) => {
  return (
    <SuspenseBoundary>
      <KnowledgeCardsList articles={articles} onArticleClick={onArticleClick} />
    </SuspenseBoundary>
  );
};

const KnowledgeCardsList = ({ articles, onArticleClick }: ArticleListProps) => {
  const { t } = useTranslation();
  const knowledgeLinkCards = useKnowledgePortal().data?.results || [];

  const isViewEmpty = knowledgeLinkCards && knowledgeLinkCards.length === 0 && articles && articles.length === 0;

  return (
    <Stack direction='row' spacing={6} className='flex-wrap w-full py-5'>
      {isViewEmpty ? (
        <EmptyView />
      ) : (
        <>
          {knowledgeLinkCards.map((card) => (
            <div data-testid='link-cards-section' key={card.id}>
              <LinkCard
                title={card.title}
                thumbnailUrl={card.thumbnail_url}
                pillText={t('knowledgeBase.linkPillLabel')}
                href={card.url}
                className='w-[380px]'
              />
            </div>
          ))}
          {articles.map((article) => (
            <div data-testid='article-cards-section' key={article.id}>
              <ArticleCard article={article} onClick={onArticleClick} className='w-[380px]' />
            </div>
          ))}
        </>
      )}
    </Stack>
  );
};

const EmptyView = () => {
  const { t } = useTranslation();

  return (
    <Stack spacing={4} className='items-center py-12' data-testid='empty-view'>
      <RiImageLine className='w-16 h-16 text-text-tertiary' />
      <Stack spacing={2} className='text-center'>
        <span className='typography-h6 text-text-primary'>{t('knowledgeBase.noArticlesTitle')}</span>
        <span className='typography-body2 text-text-secondary'>{t('knowledgeBase.noArticlesDescription')}</span>
      </Stack>
    </Stack>
  );
};
